import { useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { addActionBreadcrumb } from '../utils/sentry';

/**
 * Hook to track component errors and performance
 */
export function useErrorTracking(componentName: string) {
  useEffect(() => {
    // Add breadcrumb when component mounts
    addActionBreadcrumb('navigation', `${componentName} mounted`);

    return () => {
      // Add breadcrumb when component unmounts
      addActionBreadcrumb('navigation', `${componentName} unmounted`);
    };
  }, [componentName]);

  /**
   * Track an action with metadata
   */
  const trackAction = useCallback(
    (actionName: string, metadata?: Record<string, any>) => {
      // Add breadcrumb for action
      addActionBreadcrumb(componentName, actionName, metadata);
    },
    [componentName]
  );

  /**
   * Track a user interaction
   */
  const trackInteraction = useCallback(
    (interactionType: string, metadata?: Record<string, any>) => {
      addActionBreadcrumb(componentName, interactionType, metadata);
    },
    [componentName]
  );

  /**
   * Report a non-fatal error with context
   */
  const reportError = useCallback(
    (error: Error, context?: Record<string, any>, level: Sentry.SeverityLevel = 'error') => {
      Sentry.withScope((scope) => {
        scope.setTag('component', componentName);
        scope.setLevel(level);
        if (context) {
          scope.setContext('error_context', context);
        }
        Sentry.captureException(error);
      });
    },
    [componentName]
  );

  /**
   * Report a message (non-error event)
   */
  const reportMessage = useCallback(
    (message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) => {
      Sentry.withScope((scope) => {
        scope.setTag('component', componentName);
        scope.setLevel(level);
        if (context) {
          scope.setContext('message_context', context);
        }
        Sentry.captureMessage(message);
      });
    },
    [componentName]
  );

  return {
    trackAction,
    trackInteraction,
    reportError,
    reportMessage,
  };
}

/**
 * Hook for tracking authentication-related events
 */
export function useAuthTracking() {
  const trackLogin = (method: 'email' | 'social', success: boolean) => {
    addActionBreadcrumb('auth', 'login_attempt', {
      method,
      success,
    });

    if (!success) {
      Sentry.captureMessage('Login failed', 'warning');
    }
  };

  const trackRegistration = (role: string, success: boolean) => {
    addActionBreadcrumb('auth', 'registration_attempt', {
      role,
      success,
    });

    if (!success) {
      Sentry.captureMessage('Registration failed', 'warning');
    }
  };

  const trackPasswordReset = (stage: 'request' | 'complete', success: boolean) => {
    addActionBreadcrumb('auth', `password_reset_${stage}`, {
      success,
    });
  };

  return {
    trackLogin,
    trackRegistration,
    trackPasswordReset,
  };
}

/**
 * Hook for tracking rowing-specific features
 */
export function useRowingTracking() {
  const trackProfileUpdate = (fields: string[], success: boolean) => {
    addActionBreadcrumb('profile', 'update_attempt', {
      fields: fields.length,
      fieldsUpdated: fields,
      success,
    });
  };

  const trackSchoolInteraction = (
    action: 'view' | 'save' | 'compare' | 'contact',
    schoolId: string
  ) => {
    addActionBreadcrumb('schools', action, {
      schoolId,
    });
  };

  const trackErgScore = (scoreType: '2k' | '6k', _time: string) => {
    // Don't log actual times (PII), just that it was entered
    addActionBreadcrumb('profile', 'erg_score_entered', {
      scoreType,
      hasScore: true,
    });
  };

  const trackRecruitmentAction = (
    action: 'message_sent' | 'document_uploaded' | 'visit_scheduled',
    metadata?: Record<string, any>
  ) => {
    addActionBreadcrumb('recruitment', action, metadata);
  };

  return {
    trackProfileUpdate,
    trackSchoolInteraction,
    trackErgScore,
    trackRecruitmentAction,
  };
}
